import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiErr, fileUrl } from "../lib/api";
import { downscaleImage } from "../lib/img";
import PhotoCropDialog from "../components/PhotoCropDialog";
import PageHeader from "../components/PageHeader";
import { Plus, Loader2, Pencil, Plane, Home as HomeIcon, Star, Crown, ImagePlus, Sparkles, UserPlus, Mail, Send, Shield } from "lucide-react";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const PERMS = [
  { key: "news", label: "Post news & dispatches" },
  { key: "prayer", label: "Manage prayer feed" },
  { key: "finances", label: "Manage finances & receipts" },
  { key: "announcements", label: "Post special announcements" },
];
const emptyForm = { name: "", email: "", title: "", bio: "", location_type: "traveling", show_on_team: true, is_team_lead: false, full_admin: false, photo_file_id: null, permissions: { news: false, prayer: false, finances: false, announcements: false } };

const Avatar = ({ m, size = "h-14 w-14" }) => (
  m.photo_file_id
    ? <img src={fileUrl(`/api/files/${m.photo_file_id}`, 200)} alt={m.name} className={`${size} rounded-2xl object-cover`} />
    : <div className={`${size} rounded-2xl bg-[var(--brand-navy)]/10 text-[var(--brand-navy)] flex items-center justify-center font-heading font-bold text-lg`}>{(m.name || "?").slice(0, 1)}</div>
);

export default function Team() {
  const { user, org, isOwner } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPro = org?.team_enabled ?? ["pro", "org"].includes(org?.plan);
  const [roster, setRoster] = useState([]);
  const [manage, setManage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/team"); setRoster(r.data);
      if (isOwner && isPro) { const mgr = await api.get("/team/manage"); setManage(mgr.data); }
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Team features are on the Pro plan");
      else toast.error(apiErr(e));
    }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setDialog({ mode: "create" }); };
  const openEdit = (m) => {
    setForm({ name: m.name || "", email: m.email || "", title: m.title || "", bio: m.bio || "",
      location_type: m.location_type || "traveling", show_on_team: m.show_on_team !== false,
      is_team_lead: !!m.is_team_lead, full_admin: !!m.full_admin, photo_file_id: m.photo_file_id || null,
      permissions: { news: false, prayer: false, finances: false, announcements: false, ...(m.permissions || {}) } });
    setDialog({ mode: "edit", id: m.id });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setCropFile(file);
  };

  const onCropped = async (blob) => {
    setCropFile(null);
    setUploading(true);
    try {
      const compressed = await downscaleImage(blob, 512, 0.9);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_file_id: data.file_id }));
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim() || (dialog.mode === "create" && !form.email.trim())) return toast.error("Name and email required");
    setSaving(true);
    try {
      if (dialog.mode === "create") await api.post("/team", form);
      else await api.put(`/team/${dialog.id}`, form);
      setDialog(null); toast.success("Team saved"); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/team/${delTarget.id}`); setDelTarget(null); toast.success("Removed from team"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const [resending, setResending] = useState(null);
  const resendInvite = async (m) => {
    setResending(m.id);
    try { await api.post(`/team/${m.id}/resend-invite`); toast.success(`Invite re-sent to ${m.email}`); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setResending(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;

  return (
    <div data-testid="team-page">
      <PageHeader title="Our Team" subtitle="The people behind the mission" />

      {/* Admin: Pro upsell if not on paid plan */}
      {isOwner && !isPro && (
        <div data-testid="team-upgrade" className="rounded-2xl p-5 mb-5 text-white" style={{ background: "linear-gradient(135deg,#C9973A,#a6791f)" }}>
          <div className="flex items-center gap-2 font-heading font-bold text-lg"><Sparkles className="h-5 w-5" /> Build your team with Pro</div>
          <p className="text-sm text-white/85 mt-1">Add teammates and back-office support, set who can post news, prayer or finances, and get a private team channel. Available on the Pro plan.</p>
        </div>
      )}

      {/* Admin management */}
      {isOwner && isPro && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-widest font-semibold text-[var(--brand-navy)]">Manage team</span>
            <button data-testid="add-team-member-btn" onClick={openCreate} className="flex items-center gap-1 text-xs font-semibold accent-text hover:opacity-80"><Plus className="h-3.5 w-3.5" /> Add member</button>
          </div>
          {manage.length === 0 ? (
            <EmptyState
              testid="team-empty"
              icon={UserPlus}
              title="Add your first teammate"
              description="Bring on your traveling crew or home/back-office support — set who can post news, prayer, or finances."
              actionLabel="Add your first teammate"
              onAction={openCreate}
            />
          ) : (
            <div className="space-y-2">
              {manage.map((m) => (
                <div key={m.id} data-testid="team-manage-row" className="bg-white rounded-2xl p-3 shadow-[0_2px_16px_rgba(44,44,62,0.04)]">
                  <div className="flex items-center gap-3">
                    <Avatar m={m} size="h-11 w-11" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#2C2C3E] flex items-center gap-1.5 truncate">{m.name}{m.is_team_lead && <Crown className="h-3.5 w-3.5 accent-text" />}{m.full_admin && <span data-testid="team-admin-badge" className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide bg-[var(--brand-navy)] text-white rounded-full px-1.5 py-0.5"><Shield className="h-2.5 w-2.5" /> Admin</span>}</div>
                      <div className="text-xs text-muted-foreground truncate flex items-center gap-1" data-testid="team-manage-email"><Mail className="h-3 w-3 shrink-0" /> {m.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.title || "Team member"} · {m.full_admin ? "full admin access" : (Object.entries(m.permissions || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "view only")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-muted/60">
                    <button data-testid="team-resend-btn" onClick={() => resendInvite(m)} disabled={resending === m.id}
                      className="flex items-center gap-1 text-xs font-semibold accent-text hover:opacity-80 disabled:opacity-50">
                      {resending === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Resend invite
                    </button>
                    <div className="flex-1" />
                    <button data-testid="team-edit-btn" onClick={() => openEdit(m)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[var(--brand-navy)]"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button data-testid="team-delete-btn" onClick={() => setDelTarget(m)} className="text-xs text-muted-foreground hover:text-red-600">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Meet the Team roster (everyone) */}
      <div className="space-y-3">
        {roster.length === 0 && !isAdmin && (
          <p className="text-sm text-muted-foreground text-center py-8">The team will be introduced here soon.</p>
        )}
        {roster.map((m) => (
          <div key={m.id} data-testid="team-roster-card" className="bg-white rounded-2xl p-4 flex gap-3 shadow-[0_2px_16px_rgba(44,44,62,0.04)]">
            <Avatar m={m} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-heading font-bold text-[#2C2C3E]">{m.name}</span>
                {m.is_team_lead && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold accent-text"><Crown className="h-3 w-3" /> Lead</span>}
              </div>
              {m.title && <div className="text-sm text-[var(--brand-navy)] font-medium">{m.title}</div>}
              <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
                {m.location_type === "home" ? <><HomeIcon className="h-3 w-3" /> Supporting from home</> : <><Plane className="h-3 w-3" /> On the field</>}
              </div>
              {m.bio && <p className="text-sm text-muted-foreground mt-1.5">{m.bio}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[var(--brand-navy)]">{dialog?.mode === "edit" ? "Edit teammate" : "Add teammate"}</DialogTitle>
            <DialogDescription>Set their role and what they're allowed to manage.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar m={form} size="h-14 w-14" />
              <label className="text-sm rounded-full border px-3 py-1.5 cursor-pointer hover:bg-muted/40 flex items-center gap-1.5" data-testid="team-photo-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Photo
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="team-photo-input" />
              </label>
            </div>
            <div><Label className="text-sm">Name</Label><Input data-testid="team-name" className="mt-1 rounded-xl h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            {dialog?.mode === "create"
              ? <div><Label className="text-sm">Email (their login)</Label><Input data-testid="team-email" className="mt-1 rounded-xl h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              : <div><Label className="text-sm">Email (their login)</Label><Input data-testid="team-email-readonly" disabled className="mt-1 rounded-xl h-11 bg-muted/40" value={form.email} /><p className="text-[11px] text-muted-foreground mt-1">They sign in with this email + a one-time code. Email can't be changed here — remove & re-add to use a different one.</p></div>}
            <div><Label className="text-sm">Title / role</Label><Input data-testid="team-title" className="mt-1 rounded-xl h-11" placeholder="e.g. Logistics Lead" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="text-sm">Short bio</Label><Textarea data-testid="team-bio" className="mt-1 rounded-xl" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
            <div>
              <Label className="text-sm">Role type</Label>
              <div className="mt-1 flex gap-2">
                {[{ v: "traveling", l: "On the field", Icon: Plane }, { v: "home", l: "Home / back-office", Icon: HomeIcon }].map(({ v, l, Icon }) => (
                  <button key={v} type="button" data-testid={`team-loc-${v}`} onClick={() => setForm({ ...form, location_type: v })}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl border p-2.5 text-sm transition-colors ${form.location_type === v ? "accent-bg text-white border-transparent" : "hover:bg-muted/40"}`}>
                    <Icon className="h-4 w-4" /> {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border-2 p-3 space-y-1" style={{ borderColor: form.full_admin ? "var(--brand-navy)" : "transparent", background: form.full_admin ? "var(--brand-navy)/5" : "rgba(0,0,0,0.03)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[var(--brand-navy)]" />
                  <Label className="text-sm font-semibold text-[#2C2C3E]">Full admin access</Label>
                </div>
                <Switch data-testid="team-full-admin-switch" checked={form.full_admin} onCheckedChange={(v) => setForm({ ...form, full_admin: v })} />
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">Makes this person a co-admin with full access to trips, budget, partners, gifts, prayer and announcements — everything except organization settings and managing the team.</p>
            </div>
            {!form.full_admin && (
              <div className="rounded-2xl bg-muted/40 p-3 space-y-2.5">
                <div className="text-sm font-medium text-[#2C2C3E]">What can they do?</div>
                {PERMS.map((p) => (
                  <div key={p.key} className="flex items-center justify-between">
                    <Label className="text-sm font-normal">{p.label}</Label>
                    <Switch data-testid={`team-perm-${p.key}`} checked={!!form.permissions[p.key]} onCheckedChange={(v) => setForm({ ...form, permissions: { ...form.permissions, [p.key]: v } })} />
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between"><Label className="text-sm">Team lead</Label><Switch data-testid="team-lead-switch" checked={form.is_team_lead} onCheckedChange={(v) => setForm({ ...form, is_team_lead: v })} /></div>
            <div className="flex items-center justify-between"><Label className="text-sm">Show in "Meet the Team"</Label><Switch data-testid="team-show-switch" checked={form.show_on_team} onCheckedChange={(v) => setForm({ ...form, show_on_team: v })} /></div>
            <Button data-testid="team-submit-btn" disabled={saving} onClick={save} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save teammate"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {cropFile && <PhotoCropDialog file={cropFile} onCancel={() => setCropFile(null)} onCropped={onCropped} />}

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="team-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[var(--brand-navy)]">Remove teammate?</AlertDialogTitle>
            <AlertDialogDescription>{delTarget?.name} will lose team access and be removed from "Meet the Team".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="team-delete-confirm" onClick={(e) => { e.preventDefault(); remove(); }} className="rounded-full bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
